import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';


/* =========================================================
   DRAVENT — COLOR / ALT HELPERS
   ========================================================= */

function normalizeDraventText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

const draventColorAliases = {
  amarillo: ['amarillo', 'yellow'],
  yellow: ['amarillo', 'yellow'],

  negro: ['negro', 'black'],
  black: ['negro', 'black'],

  blanco: ['blanco', 'white'],
  white: ['blanco', 'white'],

  rojo: ['rojo', 'red'],
  red: ['rojo', 'red'],

  vinotinto: ['vinotinto', 'borgona', 'burgundy'],
  borgona: ['vinotinto', 'borgona', 'burgundy'],
  burgundy: ['vinotinto', 'borgona', 'burgundy'],

  verdeoliva: ['verdeoliva', 'oliva', 'olive'],
  oliva: ['verdeoliva', 'oliva', 'olive'],
  olive: ['verdeoliva', 'oliva', 'olive'],

  verdemilitar: ['verdemilitar', 'militar', 'militarygreen'],
  militar: ['verdemilitar', 'militar', 'militarygreen'],
  militarygreen: ['verdemilitar', 'militar', 'militarygreen'],

  verdebosque: ['verdebosque', 'bosque', 'forest', 'forestgreen'],
  bosque: ['verdebosque', 'bosque', 'forest', 'forestgreen'],
  forest: ['verdebosque', 'bosque', 'forest', 'forestgreen'],
  forestgreen: ['verdebosque', 'bosque', 'forest', 'forestgreen'],

  verde: ['verde', 'green'],
  green: ['verde', 'green'],

  azulnavy: ['azulnavy', 'navy', 'navyblue'],
  navy: ['azulnavy', 'navy', 'navyblue'],
  navyblue: ['azulnavy', 'navy', 'navyblue'],

  azuloscuro: ['azuloscuro', 'darkblue'],
  darkblue: ['azuloscuro', 'darkblue'],

  azul: ['azul', 'blue'],
  blue: ['azul', 'blue'],

  celeste: ['celeste', 'lightblue', 'skyblue'],
  lightblue: ['celeste', 'lightblue', 'skyblue'],
  skyblue: ['celeste', 'lightblue', 'skyblue'],

  grisoscuro: ['grisoscuro', 'darkgray', 'darkgrey'],
  darkgray: ['grisoscuro', 'darkgray', 'darkgrey'],
  darkgrey: ['grisoscuro', 'darkgray', 'darkgrey'],

  gris: ['gris', 'gray', 'grey'],
  gray: ['gris', 'gray', 'grey'],
  grey: ['gris', 'gray', 'grey'],

  beige: ['beige'],

  arena: ['arena', 'sand'],
  sand: ['arena', 'sand'],

  cafe: ['cafe', 'marron', 'brown'],
  marron: ['cafe', 'marron', 'brown'],
  brown: ['cafe', 'marron', 'brown'],

  crema: ['crema', 'cream'],
  cream: ['crema', 'cream'],

  lila: ['lila', 'lavanda', 'lavender'],
  lavanda: ['lila', 'lavanda', 'lavender'],
  lavender: ['lila', 'lavanda', 'lavender'],

  morado: ['morado', 'purple'],
  purple: ['morado', 'purple'],

  rosado: ['rosado', 'rosa', 'pink'],
  rosa: ['rosado', 'rosa', 'pink'],
  pink: ['rosado', 'rosa', 'pink'],

  naranja: ['naranja', 'orange'],
  orange: ['naranja', 'orange'],
};

function getDraventColorAliases(colorName) {
  const key = normalizeDraventText(colorName);
  return (draventColorAliases[key] || [key]).map(normalizeDraventText).filter(Boolean);
}


/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 * Extends ProductComponent to automatically emit product:view events when visible.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    if (event.defaultPrevented) return;

    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (image) => image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}


/**
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink
 * @property {import('slideshow').Slideshow} [slideshow]
 * @property {import('quick-add').QuickAddComponent} [quickAdd]
 * @property {HTMLElement} [cardGallery]
 * @property {HTMLImageElement[]} [imagesToTransition]
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    const link = this.refs.productCardLink;
    if (!link.getAttribute('href')) return '';

    const url = new URL(link.href);
    const variantOverride = this.#getProductPageVariantOverride();

    if (variantOverride?.action === 'set') {
      url.searchParams.set('variant', variantOverride.variantId);
    } else if (variantOverride?.action === 'remove') {
      url.searchParams.delete('variant');
    }

    return url.toString();
  }

  getSelectedVariantId() {
    const variantOverride = this.#getProductPageVariantOverride();
    return variantOverride?.action === 'set' ? variantOverride.variantId : null;
  }

  #getProductPageVariantOverride() {
    const link = this.refs.productCardLink;

    if (link.getAttribute('href')) {
      const url = new URL(link.href);
      if (url.searchParams.has('variant')) return null;
    }

    const variantId = this.#getCheckedVariantId();
    if (variantId === undefined) return null;

    return variantId ? { action: 'set', variantId } : { action: 'remove' };
  }

  #getCheckedVariantId() {
    const checkedInput = this.querySelector('input[type="radio"]:checked[data-variant-id]');
    return checkedInput?.dataset.variantId;
  }

  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  #navigateToURL = (event, url) => {
    const shouldOpenInNewTab =
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    }

    window.location.href = url.href;
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;

    if (!(link instanceof HTMLAnchorElement)) {
      throw new Error('Product card link not found');
    }

    this.#handleQuickAdd();

    /*
     * DRAVENT:
     * filtrar inmediatamente la tarjeta al color inicial.
     */
    requestAnimationFrame(() => {
      const initialColor = this.refs.cardGallery?.dataset.draventInitialColor;

      if (initialColor) {
        this.updateDraventGalleryByColor(initialColor);
      }
    });

    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];

    currentSlide?.nextElementSibling
      ?.querySelector('img[loading="lazy"]')
      ?.removeAttribute('loading');
  }

  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the product select event.
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    const { optionValueId, variantId, connectedProductUrl } = event.detail ?? {};

    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    if (typeof variantId === 'string') {
      this.applyVariantToLinks(
        variantId,
        typeof connectedProductUrl === 'string' ? connectedProductUrl : undefined
      );
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        this.updatePrice(html);
        this.#isUnavailableVariantSelected(html);

        this.#updateProductUrl(
          html,
          typeof variantId === 'string' ? variantId : undefined
        );

        this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        /*
         * DRAVENT:
         * No usamos #updateVariantImages().
         * Esa función nativa trabaja con una sola imagen
         * asociada a una variante.
         *
         * Nosotros usamos varias imágenes por color mediante ALT.
         */

        this.#previousSlideIndex = null;

        this.removeAttribute('data-no-swatch-selected');

        this.#updateOverflowList();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[product-card] Event promise rejected:', error);
        }
      });
  };

  #updateOverflowList() {
    const overflowList = this.querySelector(
      'swatches-variant-picker-component overflow-list'
    );

    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]')
      ? true
      : false;

    if (!overflowList || !isActiveOverflowList) return;

    requestAnimationFrame(() => {
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {Document} html
   */
  updatePrice(html) {
    const priceContainer = this.querySelectorAll(
      `product-price [ref='priceContainer']`
    )[1];

    const newPriceElement = html.querySelector(
      `product-price [ref='priceContainer']`
    );

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * DRAVENT:
   * muestra únicamente las fotografías cuyo ALT
   * corresponde al color seleccionado.
   *
   * Negro -> color-black
   * Beige -> color-beige
   *
   * @param {string} colorName
   */
  updateDraventGalleryByColor(colorName) {
    const { slideshow, cardGallery } = this.refs;

    if (!slideshow || !cardGallery || !colorName) return;

    const slides = Array.from(
      cardGallery.querySelectorAll(
        'slideshow-slide[data-dravent-media-alt]'
      )
    );

    if (!slides.length) return;

    const aliases = getDraventColorAliases(colorName);

    if (!aliases.length) return;

    const matchingSlides = slides.filter((slide) => {
      const mediaAlt = normalizeDraventText(
        slide.dataset.draventMediaAlt
      );

      return aliases.some((alias) =>
        mediaAlt.includes(`color${alias}`)
      );
    });

    /*
     * Si el color no tiene fotos correctamente etiquetadas,
     * conservamos la galería actual.
     */
    if (!matchingSlides.length) return;

    const matchingSet = new Set(matchingSlides);

    for (const slide of slides) {
      slide.hidden = !matchingSet.has(slide);
    }

    const firstSlide = matchingSlides[0];
    const firstSlideId = firstSlide.getAttribute('slide-id');

    if (firstSlideId) {
      slideshow.select(
        { id: firstSlideId },
        undefined,
        { animate: false }
      );
    } else {
      const index = slides.indexOf(firstSlide);

      if (index >= 0) {
        slideshow.select(
          index,
          undefined,
          { animate: false }
        );
      }
    }

    this.#previousSlideIndex = null;
  }

  #updateProductUrl(html, intendedVariantId) {
    const responseProductCard = html.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute(
      'data-featured-media-url'
    );

    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const responseUrl = new URL(anchorElement.href);

      this.#updateLinks({
        pathname: responseUrl.pathname,
        variantId:
          intendedVariantId ??
          responseUrl.searchParams.get('variant'),
      });
    }
  }

  applyVariantToLinks(variantId, productUrl) {
    let pathname;

    if (productUrl) {
      try {
        pathname = new URL(
          productUrl,
          window.location.origin
        ).pathname;
      } catch (error) {
        console.warn(
          '[product-card] Invalid product URL:',
          productUrl,
          error
        );

        return;
      }
    }

    this.#updateLinks({
      pathname,
      variantId,
    });
  }

  #updateLinks({ pathname, variantId }) {
    const {
      productCardLink,
      productTitleLink,
      cardGalleryLink,
    } = this.refs;

    for (const linkEl of [
      productCardLink,
      cardGalleryLink,
      productTitleLink,
    ]) {
      if (!(linkEl instanceof HTMLAnchorElement) || !linkEl.href) continue;

      const url = new URL(linkEl.href);

      if (pathname) {
        url.pathname = pathname;
      }

      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }

      linkEl.href = url.toString();
    }
  }

  #isUnavailableVariantSelected(html) {
    const allVariants = html.querySelectorAll('input:checked');

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(
        variant.dataset.optionAvailable === 'true'
      );
    }
  }

  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector(
      '.add-to-cart__button button'
    );

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /*
   * Se conserva la función nativa por compatibilidad,
   * pero Dravent no la ejecuta.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;

    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId =
      this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden =
          slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select(
        { id: selectedImageId },
        undefined,
        { animate: false }
      );
    }
  }

  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  get variantPicker() {
    return this.querySelector(
      'swatches-variant-picker-component'
    );
  }

  #previousSlideIndex = null;

  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    slideshow.select(
      { id },
      undefined,
      { animate: false }
    );
  }

  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (
      this.#previousSlideIndex != null &&
      this.#previousSlideIndex > 0
    ) {
      slideshow.select(
        this.#previousSlideIndex,
        undefined,
        { animate: false }
      );
    } else {
      slideshow.next(
        undefined,
        { animate: false }
      );

      setTimeout(() =>
        this.#preloadNextPreviewImage()
      );
    }
  }

  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    if (!this.variantPicker) {
      const { slideshow } = this.refs;

      if (!slideshow) return;

      slideshow.previous(
        undefined,
        { animate: false }
      );

      return;
    }

    this.#resetVariant();
  }

  /**
   * DRAVENT:
   * volver al color actualmente seleccionado.
   */
  #resetVariant = () => {
    const checkedSwatch = this.querySelector(
      'swatches-variant-picker-component input[type="radio"]:checked'
    );

    if (checkedSwatch instanceof HTMLInputElement) {
      const colorName =
        checkedSwatch.value ||
        checkedSwatch.dataset.optionValue ||
        checkedSwatch.getAttribute('aria-label') ||
        checkedSwatch.getAttribute('title') ||
        '';

      if (colorName) {
        this.updateDraventGalleryByColor(colorName);
        return;
      }
    }

    const initialColor =
      this.refs.cardGallery?.dataset.draventInitialColor;

    if (initialColor) {
      this.updateDraventGalleryByColor(initialColor);
    }
  };

  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"]'
    );

    if (interactiveElement) return;

    const link = this.refs.productCardLink;

    if (!link.href) return;

    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');

    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest(
      'results-list[infinite-scroll="true"]'
    );

    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');

      url.hash = productCardAnchor;

      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState(
          {},
          '',
          url.toString()
        );
      });
    }

    const targetLink = event.target.closest('a');

    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  resetVariant = debounce(
    this.#resetVariant,
    100
  );
}

if (!customElements.get('product-card')) {
  customElements.define(
    'product-card',
    ProductCard
  );
}


/* =========================================================
   DRAVENT — SWATCHES
   ========================================================= */

/**
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const isSwatchInput =
      event.target instanceof HTMLInputElement &&
      event.target.name?.includes('-swatch');

    const clickedSwatch = event.target;

    const hasAvailableVariant =
      clickedSwatch.dataset.hasAvailableVariant === 'true';

    const firstAvailableVariantId =
      clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    if (
      isSwatchInput &&
      hasAvailableVariant &&
      firstAvailableVariantId
    ) {
      event.stopPropagation();

      this.updateSelectedOption(clickedSwatch);

      /*
       * DRAVENT:
       * cambiar inmediatamente la galería
       * usando el nombre del color pulsado.
       */
      const productCard =
        this.closest('product-card');

      if (productCard instanceof ProductCard) {
        const colorName =
          clickedSwatch.value ||
          clickedSwatch.dataset.optionValue ||
          clickedSwatch.getAttribute('aria-label') ||
          clickedSwatch.getAttribute('title') ||
          '';

        productCard.updateDraventGalleryByColor(
          colorName
        );
      }

      const optionValueId =
        clickedSwatch.dataset.optionValueId || '';

      const connectedProductUrl =
        clickedSwatch.dataset.connectedProductUrl || '';

      const requestUrl = this.buildRequestUrl(
        clickedSwatch,
        'product-card',
        [optionValueId]
      );

      this.fetchUpdatedSection(requestUrl, {
        detail: {
          optionValueId,
          variantId: firstAvailableVariantId,
          connectedProductUrl,
        },
      });

      return;
    }

    super.variantChanged(event);
  }

  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define(
    'swatches-variant-picker-component',
    SwatchesVariantPickerComponent
  );
}